/**
 * @fileoverview AudioPlayer component for playing audio files in the Storyteller web application.
 * This component provides controls for playing, pausing, and displaying progress of audio recordings.
 *
 * @example
 * // Basic usage
 * <AudioPlayer
 *   audio={{ file: 'path/to/audio.mp3', duration: 100 }}
 *   titles={{ start: 'Start', stop: 'Stop' }}
 *   isDisabled={false}
 * />
 */

import { useState } from 'react'
import css from './AudioPlayer.module.css'

import { useActivityPing } from '../../hooks/useActivityPing'
import { PlayerButton } from '../base/PlayerButton'
import { ProcessingButton } from '../base/ProcessingButton'
import { ProgressButton } from '../base/ProgressButton'

/**
 * @interface AudioPlayerProps
 * @description Props for the AudioPlayer component.
 * @property {Object} audio - The audio object containing the file and duration.
 * @property {string} audio.file - The path to the audio file.
 * @property {number} audio.duration - The duration of the audio file in seconds.
 * @property {Object} titles - The titles for the start and stop buttons.
 * @property {string} titles.start - The title for the start button.
 * @property {string} titles.stop - The title for the stop button.
 * @property {boolean} isDisabled - Whether the audio player is disabled.
 * @property {Function} onStarted - A function to handle the start action.
 * @property {Function} onEnded - A function to handle the end action.
 */
export interface AudioPlayerProps extends React.HTMLAttributes<HTMLDivElement> {
    audio: {
        file: string
        duration: number
    }
    titles: Record<'start' | 'stop', string>
    isDisabled: boolean
    onStarted?: () => void
    onEnded?: () => void
}

/**
 * @function AudioPlayer
 * @description Component for playing audio files with controls.
 * @param {AudioPlayerProps} props - The props for the AudioPlayer component.
 * @returns {JSX.Element} The AudioPlayer component.
 */
export function AudioPlayer(props: AudioPlayerProps) {
    const [isReady, setIsReady] = useState<boolean>(false)
    const [isPlaying, setIsPlaying] = useState<boolean>(false)
    const ping = useActivityPing()

    const audio = new Audio()

    audio.addEventListener('loadedmetadata', () => {
        setIsReady(true)
    })

    audio.src = props.audio.file

    async function onPlay() {
        await audio.play()

        audio.addEventListener('ended', () => {
            props.onEnded?.()
            setIsPlaying(false)
        })

        setIsPlaying(true)
        props.onStarted?.()

        await ping()
    }

    let button

    if (!isReady) {
        button = <ProcessingButton />
    }

    if (isReady && !isPlaying) {
        button = (
            <PlayerButton
                isDisabled={props.isDisabled}
                onClick={onPlay}
                label={props.titles.start}
            />
        )
    }

    if (isReady && isPlaying) {
        button = <ProgressButton duration={props.audio.duration} label={props.titles.stop} />
    }

    return (
        <div style={props.style} className={css.container}>
            {button}
        </div>
    )
}
